import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getZoneList, getPrecinctOfAddress } from '../../../services/procedureService';

import Modal from '../../../components/uielements/modal';
import SearchableSelect from '../../../components/uielements/selectComponent/searchableSelect';

import styled from 'styled-components';
import { key } from 'styled-theme';

const ModalContent = styled.div`
  padding: 24px;
  min-height: 300px;
`;

const ModalHeader = styled.h2`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ModalDescription = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #363535;
  line-height: 1.5;
`;

const SelectContainer = styled.div`
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(90deg, #7b5cff, #60a5fa);
  color: white;

  &:hover:not(:disabled) {
    background: linear-gradient(90deg, #7b5cff, #60a5fa);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #f3f4f6;
  color: #374151;

  &:hover:not(:disabled) {
    background-color: #e5e7eb;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const InfoBox = styled.div`
  padding: 12px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  color: #1e40af;
  font-size: 13px;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const SelectionSummary = styled.div`
  padding: 12px;
  background-color: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 13px;
  color: #166534;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #15803d;
  }
`;

const ZonePrecinctSelectionModal = ({ isOpen, onClose, onConfirm, procedureId }) => {
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedPrecinct, setSelectedPrecinct] = useState(null);

  // Fetch zones using React Query
  const {
    data: zonesData = [],
    isLoading: isLoadingZones,
    isError: isZonesError,
    error: zonesError,
    refetch: refetchZones,
  } = useQuery({
    queryKey: ['zones'],
    queryFn: async () => {
      const response = await getZoneList();
      return response;
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error('Error fetching zones:', error);
    },
  });

  // Fetch precincts based on selected zone
  const {
    data: precinctsData = [],
    isLoading: isLoadingPrecincts,
    isError: isPrecinctsError,
    error: precinctsError,
    refetch: refetchPrecincts,
  } = useQuery({
    queryKey: ['precincts', selectedZone?._id],
    queryFn: async () => {
      if (!selectedZone?._id) {
        throw new Error('Zone ID is required');
      }
      const response = await getPrecinctOfAddress({ regionId: selectedZone._id });
      return response;
    },
    enabled: !!selectedZone?._id && isOpen,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    onError: (error) => {
      console.error('Error fetching precincts:', error);
    },
  });

  // Transform zones for SearchableSelect
  const zoneOptions = useMemo(() => {
    if (!zonesData || zonesData.length === 0) return [];

    return zonesData.map((zone) => ({
      ...zone,
      procedureName: zone.regionName || zone.name || 'Unnamed Zone',
      value: zone._id || zone.id,
    }));
  }, [zonesData]);

  // Transform precincts for SearchableSelect
  const precinctOptions = useMemo(() => {
    if (!precinctsData || precinctsData.length === 0) return [];

    return precinctsData.map((precinct) => ({
      ...precinct,
      procedureName: precinct.precinctName || precinct.name || 'Unnamed Precinct',
      value: precinct._id || precinct.id,
    }));
  }, [precinctsData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedZone(null);
      setSelectedPrecinct(null);
    }
  }, [isOpen]);

  const handleZoneChange = (zone) => {
    setSelectedZone(zone);
    setSelectedPrecinct(null); // Reset precinct when zone changes
  };

  const handlePrecinctChange = (precinct) => {
    setSelectedPrecinct(precinct);
  };

  const handleConfirm = () => {
    if (selectedZone && selectedPrecinct) {
      // Pass both zone and precinct objects to parent
      onConfirm(selectedZone, selectedPrecinct);

      // Reset state after confirmation
      handleReset();
    }
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setSelectedZone(null);
    setSelectedPrecinct(null);
  };

  const handleRetryZones = () => {
    refetchZones();
  };

  const handleRetryPrecincts = () => {
    refetchPrecincts();
  };

  const isConfirmDisabled = !selectedZone || !selectedPrecinct || isLoadingPrecincts;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} width="600px" closeOnOverlayClick={false}>
      <ModalContent>
        <ModalHeader>Select Zone and Precinct</ModalHeader>

        <ModalDescription>
          We couldn&apos;t automatically determine the zone for this address. Please manually select
          the zone and precinct to continue with procedure verification.
        </ModalDescription>

        <InfoBox>
          <strong>Note:</strong> The zone and precinct selection is required to verify if your
          selected procedure is permitted in this area. Both fields must be completed.
        </InfoBox>

        {/* Zones Error */}
        {isZonesError && (
          <ErrorMessage role="alert">
            <strong>Failed to load zones:</strong> {zonesError?.message || 'Unknown error'}
            <br />
            <SecondaryButton
              onClick={handleRetryZones}
              style={{ marginTop: '8px', padding: '6px 12px', fontSize: '12px' }}
            >
              Retry
            </SecondaryButton>
          </ErrorMessage>
        )}

        {/* Precincts Error */}
        {isPrecinctsError && selectedZone && (
          <ErrorMessage role="alert">
            <strong>Failed to load precincts:</strong> {precinctsError?.message || 'Unknown error'}
            <br />
            <SecondaryButton
              onClick={handleRetryPrecincts}
              style={{ marginTop: '8px', padding: '6px 12px', fontSize: '12px' }}
            >
              Retry
            </SecondaryButton>
          </ErrorMessage>
        )}

        {/* Zone Selection */}
        <SelectContainer>
          <SearchableSelect
            options={zoneOptions}
            selectedOption={handleZoneChange}
            label="Select Zone *"
            placeholder={isLoadingZones ? 'Loading zones...' : 'Choose a zone'}
            width="100%"
            sizevalue="medium"
            loading={isLoadingZones}
            disabled={isLoadingZones || isZonesError}
            id="zone-selector"
          />
        </SelectContainer>

        {/* Precinct Selection */}
        <SelectContainer>
          <SearchableSelect
            options={precinctOptions}
            selectedOption={handlePrecinctChange}
            label="Select Precinct *"
            placeholder={
              !selectedZone
                ? 'Please select a zone first'
                : isLoadingPrecincts
                  ? 'Loading precincts...'
                  : precinctOptions.length === 0 && !isLoadingPrecincts
                    ? 'No precincts available'
                    : 'Choose a precinct'
            }
            width="100%"
            sizevalue="medium"
            loading={isLoadingPrecincts}
            disabled={!selectedZone || isLoadingPrecincts || isPrecinctsError}
            id="precinct-selector"
          />
        </SelectContainer>

        {/* No Precincts Warning */}
        {selectedZone &&
          precinctOptions.length === 0 &&
          !isLoadingPrecincts &&
          !isPrecinctsError && (
            <InfoBox
              style={{ backgroundColor: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }}
            >
              <strong>⚠️ No precincts found</strong>
              <br />
              No precincts are available for the selected zone. Please try selecting a different
              zone.
            </InfoBox>
          )}

        {/* Selection Summary */}
        {selectedZone && selectedPrecinct && (
          <SelectionSummary>
            <strong>✓ Your Selection:</strong>
            <div>Zone: {selectedZone.regionName || selectedZone.name}</div>
            <div>Precinct: {selectedPrecinct.precinctName || selectedPrecinct.name}</div>
          </SelectionSummary>
        )}

        {/* Action Buttons */}
        <ButtonGroup>
          <SecondaryButton onClick={handleCancel} type="button">
            Cancel
          </SecondaryButton>

          <PrimaryButton
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            type="button"
            title={
              !selectedZone
                ? 'Please select a zone'
                : !selectedPrecinct
                  ? 'Please select a precinct'
                  : 'Confirm your selection'
            }
          >
            {isLoadingPrecincts && <LoadingSpinner />}
            Confirm Selection
          </PrimaryButton>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default ZonePrecinctSelectionModal;
